import React, { useEffect, useState, useRef } from "react";
import UserRepository from '../../../core/repository/UserRepository';
import {
  Box,
  IconButton,
  Tooltip,
  Button,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery, useTheme
} from "@mui/material";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import FastForwardIcon from "@mui/icons-material/FastForward";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { useSelector } from "react-redux";
import _ from "lodash";
import { useWatermarkProtection } from '../../../hook/useVideoWatermarkProtection'; 

const VideoPlayerComponent = ({
  videoUrl,
  tumbnailUrl,
  volume,
  muted,
  path,
}) => {
  const [userInfo, setUserInfo] = useState({ name: '', contact: '', email: '' });
  const [watermarkKey, setWatermarkKey] = useState(0);
  const hasTriggeredRef = useRef(false);

  const sendTamperingAlert = async () => {
    try {
      const res = await UserRepository.temperingAlertMail();
      console.log("Tampering alert email sent:", res);
    } catch (e) {
      console.error("Tampering alert error:", e);
    }
  };

  useWatermarkProtection({
    wrapperSelector: '.watermark-protection',
    watermarkSelector: '.w-watermark',
    onTamper: () => {
    if (!hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      sendTamperingAlert(); 
    }
     setWatermarkKey(prev => prev + 1);
  }
  });

  useEffect(() => {
    const getUser = async () => {
      const user = await UserRepository.getUserDetail();
      setUserInfo({
        name: `${user?.data?.first_name || ""} ${user?.data?.last_name || ""}`.trim(),
        contact: user?.data?.phone_number || '',
        email: user?.data?.email || '',
      });
    };
    getUser();
  }, []);
  const verifyData = useSelector((state) => state.auth.get("verifyData"));
  const [cloudfronturl, setCloduFrontUrl] = useState("");
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [speedMenuAnchor, setSpeedMenuAnchor] = useState(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hideControlsTimeout = useRef(null);

  const speedOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  useEffect(() => {
    setCloduFrontUrl(_.get(verifyData, "video_url"));
  }, [verifyData]);

  const handleMouseMove = () => {
    setShowControls(true);
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    hideControlsTimeout.current = setTimeout(() => setShowControls(false), 3000);
  };

  const handleMouseLeave = () => {
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    hideControlsTimeout.current = setTimeout(() => {
      setShowControls(false);
    }, 1000);
  };

  // Monitor fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      setIsFullscreen(!!fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  const handleForward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime += 10;
    }
  };

  const handleBackward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime -= 10;
    }
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
    }
    setSpeedMenuAnchor(null);
  };

  const handleSpeedMenuOpen = (event) => {
    if (speedMenuAnchor) {
      // If menu is already open, close it (toggle behavior)
      setSpeedMenuAnchor(null);
    } else {
      // If menu is closed, open it
      setSpeedMenuAnchor(event.currentTarget);
    }
  };

  const handleSpeedMenuClose = () => {
    setSpeedMenuAnchor(null);
  };

  const handleFullscreenToggle = () => {
    const container = containerRef.current;

    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    if (isIOS) {
      // On iOS, use custom fullscreen (CSS-based) to keep watermark visible
      if (!isFullscreen) {
        // Enter custom fullscreen
        if (container.requestFullscreen) {
          container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
      }
      return;
    }

    if (!isFullscreen) {
      // Enter fullscreen
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.mozRequestFullScreen) {
        container.mozRequestFullScreen();
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  return (
    <Box
      className="watermark-protection"
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        backgroundColor: "black",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        alignSelf: "center",
        marginBottom: 2,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Custom Watermark Overlay */}
      <Box
        key={watermarkKey}
        className="w-watermark" // Added target class for hook
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          zIndex: 5,
          overflow: 'hidden',
          display: 'block', 
          opacity: 1,
        }}
      >
        {[...Array(isMobile ? 8 : 9)].map((_, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              left: `${(index % (isMobile ? 2 : 3)) * (isMobile ? 50 : 40)}%`,
              top: `${Math.floor(index / (isMobile ? 2 : 3)) * (isMobile ? 25 : 30)}%`,
              transform: 'rotate(-20deg)',
              opacity: 0.25,
              fontSize: isMobile ? '10px' : '12px',
              color: '#171515ff',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
          >
            <div 
              key={watermarkKey}>{userInfo.name}</div>
            <div 
              key={watermarkKey}>{userInfo.contact}</div>
            <div 
              key={watermarkKey}>{userInfo.email}</div>
          </Box>
        ))}
      </Box>
      
      <video
        ref={videoRef}
        controls
        playsInline
        webkit-playsinline="true"
        x5-playsinline="true"
        style={{ minHeight: 400, width: "100%", display: "block" }}
        height={"100%"}
        width={"100%"}
        controlsList="nodownload noplaybackrate nofullscreen"
        disablePictureInPicture
      >
        <source src={`${cloudfronturl}${path}`} type="video/mp4" />
        <source src={`${cloudfronturl}${path}`} type="video/ogg" />
      </video>
      {/* Controls Overlay */}
      <Box
        sx={{
          position: "absolute",
          bottom: 55,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          borderRadius: "8px",
          padding: "12px 16px",
          gap: 10,
          zIndex: 2147483647,
          opacity: showControls  ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
          pointerEvents: showControls  ? "auto" : "none",
        }}
      >
        <Tooltip title="10s backward">
            <IconButton
              onClick={handleBackward}
              sx={{
                color: "#ffffff",
                padding: "8px",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              <FastRewindIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="10s forward">
            <IconButton
              onClick={handleForward}
              sx={{
                color: "#ffffff",
                padding: "8px",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              <FastForwardIcon />
            </IconButton>
          </Tooltip>

          {/* YouTube-like Speed Control */}
          <Tooltip title="Playback speed">
            <Button
              onClick={handleSpeedMenuOpen}
              sx={{
                color: "white",
                textTransform: "none",
                minWidth: "60px",
                height: 40,
                ml: 1,
                border: "1px solid rgba(255, 255, 255, 0.3)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.5)",
                },
              }}
            >
              {playbackSpeed}x
            </Button>
          </Tooltip>

          <Popover
            open={Boolean(speedMenuAnchor)}
            anchorEl={speedMenuAnchor}
            onClose={handleSpeedMenuClose}
            container={containerRef.current}
            anchorOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            PaperProps={{
              sx: {
                backgroundColor: "rgba(28, 28, 28, 0.98)",
                color: "white",
                maxHeight: 300,
                width: 100,
                marginBottom: 1,
              },
            }}
          >
            <List sx={{ py: 0 }}>
              {speedOptions.map((speed) => (
                <ListItemButton
                  key={speed}
                  onClick={() => handleSpeedChange(speed)}
                  selected={playbackSpeed === speed}
                  sx={{
                    justifyContent: "center",
                    py: 1.5,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.25)",
                      },
                    },
                  }}
                >
                  <ListItemText
                    primary={speed === 1 ? "Normal" : `${speed}x`}
                    primaryTypographyProps={{
                      sx: {
                        textAlign: "center",
                        fontSize: "14px",
                        fontWeight: playbackSpeed === speed ? 600 : 400,
                      },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Popover>

          <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
            <IconButton
              onClick={handleFullscreenToggle}
              sx={{
                color: "#ffffff",
                padding: "8px",
                ml: 1,
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Box>
    </Box>
  );
};

export default VideoPlayerComponent;